import {
  IntradayCapSignalParams,
  IntradayMaSignalParams,
  IntradayScheduleSignalParams,
  IntradaySignalFrequency,
  IntradaySignalFrequencyUnit,
  IntradaySignalName,
  IntradaySignalParams,
} from '../../types/trading';

export type SignalMode = 'ma' | 'percent_cap' | 'dollar_cap' | 'on_schedule';

export interface SignalMeta {
  mode: SignalMode;
  name: IntradaySignalName;
  label: string;
  description: string;
}

export const SIGNAL_REGISTRY: Record<SignalMode, SignalMeta> = {
  ma: {
    mode: 'ma',
    name: 'intraday_ma_crossover',
    label: 'MA Crossover',
    description:
      'Moving-average crossover. Windows are measured in samples at a 1-second cadence (gap-filled by carry-forward).',
  },
  percent_cap: {
    mode: 'percent_cap',
    name: 'percentage_return_cap',
    label: 'Percentage Return Cap',
    description: 'Breach upper/lower percentage return thresholds against a synthetic 1-qty leg.',
  },
  dollar_cap: {
    mode: 'dollar_cap',
    name: 'dollar_return_cap',
    label: 'Dollar Return Cap',
    description: 'Breach upper/lower dollar profit thresholds against a synthetic 1-qty leg.',
  },
  on_schedule: {
    mode: 'on_schedule',
    name: 'on_schedule',
    label: 'On Schedule',
    description: 'Fires at the configured frequency (every N seconds/minutes/hours).',
  },
};

export const SIGNAL_MODE_ORDER: SignalMode[] = [
  'ma',
  'percent_cap',
  'dollar_cap',
  'on_schedule',
];

export interface FrequencyFormState {
  everyRaw: string;
  unit: IntradaySignalFrequencyUnit;
}

export interface MaFormState {
  shortWindowRaw: string;
  longWindowRaw: string;
  trend: IntradayMaSignalParams['trend'];
  frequency: FrequencyFormState;
}

export interface CapFormState {
  upperLimitRaw: string;
  lowerLimitRaw: string;
  frequency: FrequencyFormState;
}

export interface ScheduleFormState {
  frequency: FrequencyFormState;
}

const defaultFrequency = (
  every: number,
  unit: IntradaySignalFrequencyUnit,
): FrequencyFormState => ({
  everyRaw: String(every),
  unit,
});

export const defaultMaForm = (): MaFormState => ({
  shortWindowRaw: '5',
  longWindowRaw: '20',
  trend: 'upward',
  frequency: defaultFrequency(1, 'seconds'),
});

export const defaultCapForm = (): CapFormState => ({
  upperLimitRaw: '',
  lowerLimitRaw: '',
  frequency: defaultFrequency(1, 'seconds'),
});

export const defaultScheduleForm = (): ScheduleFormState => ({
  frequency: defaultFrequency(1, 'minutes'),
});

const parsePositiveInteger = (raw: string): number => {
  const value = Number(raw);
  if (!Number.isInteger(value) || value <= 0) return 0;
  return value;
};

const parseOptionalFiniteNumber = (raw: string): number | null | undefined => {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value)) return undefined;
  return value;
};

const resolveFrequency = (
  form: FrequencyFormState,
): { valid: true; value: IntradaySignalFrequency } | { valid: false; error: string } => {
  const every = parsePositiveInteger(form.everyRaw);
  if (every <= 0) {
    return { valid: false, error: "frequency 'every' must be a positive integer" };
  }
  return { valid: true, value: { every, unit: form.unit } };
};

export interface BuildStartResult {
  valid: boolean;
  params?: IntradaySignalParams;
  error?: string;
}

export const buildMaStart = (form: MaFormState): BuildStartResult => {
  const shortWindow = parsePositiveInteger(form.shortWindowRaw);
  const longWindow = parsePositiveInteger(form.longWindowRaw);
  if (shortWindow <= 0 || longWindow <= 0) {
    return { valid: false, error: 'windows must be positive integers' };
  }
  if (shortWindow >= longWindow) {
    return { valid: false, error: 'short window must be less than long window' };
  }
  const frequency = resolveFrequency(form.frequency);
  if (!frequency.valid) return { valid: false, error: frequency.error };
  const maFrequency: IntradaySignalFrequency = {
    every: 1,
    unit: frequency.value.unit,
  };
  return {
    valid: true,
    params: {
      short_window: shortWindow,
      long_window: longWindow,
      trend: form.trend,
      frequency: maFrequency,
    },
  };
};

export const buildCapStart = (
  form: CapFormState,
  mode: 'percent_cap' | 'dollar_cap',
): BuildStartResult => {
  const upper = parseOptionalFiniteNumber(form.upperLimitRaw);
  const lower = parseOptionalFiniteNumber(form.lowerLimitRaw);

  if (upper === undefined || lower === undefined) {
    return { valid: false, error: 'limits must be finite numbers' };
  }
  if (upper === null && lower === null) {
    return { valid: false, error: 'at least one of upper/lower limit is required' };
  }

  const frequency = resolveFrequency(form.frequency);
  if (!frequency.valid) return { valid: false, error: frequency.error };

  const scale = mode === 'percent_cap' ? 0.01 : 1;
  const payload: IntradayCapSignalParams = {
    upper_limit: upper === null ? null : upper * scale,
    lower_limit: lower === null ? null : lower * scale,
    frequency: frequency.value,
  };
  return { valid: true, params: payload };
};

export const buildScheduleStart = (form: ScheduleFormState): BuildStartResult => {
  const frequency = resolveFrequency(form.frequency);
  if (!frequency.valid) return { valid: false, error: frequency.error };
  const payload: IntradayScheduleSignalParams = { frequency: frequency.value };
  return { valid: true, params: payload };
};
